import axios from 'axios';

// Configuração base da API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app'  // REPORTFius backend URL
  : 'http://localhost:3001';  // Backend local em desenvolvimento

// Configurar baseURL corretamente
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000, // 30 segundos timeout
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    if (config?.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.response?.data?.error || error.message
      });
    }
    
    // Se o token expirou (401) e não é uma tentativa de renovação
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Redirecionar para login
      if (window.location.pathname !== '/login' && 
          window.location.pathname !== '/admin/login' &&
          window.location.pathname !== '/') {
        window.location.href = '/admin/login';
      }
      
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Serviços específicos da API
export const authService = {
  login: async (credentials) => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_AUTH === 'true') {
      console.log('🔐 Iniciando login...');
    }
    const response = await api.post('/auth/login', credentials);
    console.log('✅ Login realizado');
    return response;
  },
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', email),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  logout: () => {
    console.log('🚪 Fazendo logout...');
    void api.post('/auth/logout').catch(() => {});
  }
};

export const userService = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getAllUsers: () => api.get('/users'),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
};

export const systemService = {
  healthCheck: () => api.get('/health'),
};

export const agentsService = {
  getAvailableClients: () => api.get('/complaints/clients').then((response) => response.data),

  // Vector store management
  createVectorStore: (data) => api.post('/agents/vector-stores', data).then((response) => response.data),
  getVectorStore: (clientSlug) =>
    api.get('/agents/vector-stores', { params: { client_slug: clientSlug } }).then((response) => response.data),
  getAllVectorStores: () => api.get('/agents/vector-stores/all').then((response) => response.data),
  getClientFiles: (clientSlug) => api.get(`/agents/vector-stores/${clientSlug}/files`).then((response) => response.data),

  // File operations
  uploadPolicyFile: (clientSlug, formData, onUploadProgress) =>
    api.post('/agents/vector-stores/upload', formData, {
      params: { client_slug: clientSlug },
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    }).then((response) => response.data),
  deleteFile: (fileId, clientSlug) =>
    api.delete(`/agents/vector-stores/files/${fileId}`, { params: { client_slug: clientSlug } }).then((response) => response.data),

  // RAG search test
  searchVectorStore: (clientSlug, query) =>
    api.post('/agents/vector-stores/search', { client_slug: clientSlug, query }).then((response) => response.data),

  // Investigation operations
  triggerInvestigation: (complaintId) => api.post(`/agents/complaints/${complaintId}/investigate`).then((response) => response.data),
  getInvestigation: (complaintId) => api.get(`/agents/complaints/${complaintId}/investigation`).then((response) => response.data),
  approvePlan: (complaintId, approved, notes) =>
    api.post(`/agents/complaints/${complaintId}/approve-plan`, { approved, notes }).then((response) => response.data),

  // Interviews
  addInterview: (complaintId, data) => api.post(`/agents/complaints/${complaintId}/interviews`, data).then((response) => response.data),
  getInterviews: (complaintId) => api.get(`/agents/complaints/${complaintId}/interviews`).then((response) => response.data),
  deleteInterview: (complaintId, interviewId) =>
    api.delete(`/agents/complaints/${complaintId}/interviews/${interviewId}`).then((response) => response.data),

  // Report operations
  triggerReport: (complaintId) => api.post(`/agents/complaints/${complaintId}/report`).then((response) => response.data),
  getReport: (complaintId) => api.get(`/agents/complaints/${complaintId}/report`).then((response) => response.data),
  approveReport: (complaintId, approved) =>
    api.post(`/agents/complaints/${complaintId}/approve-report`, { approved }).then((response) => response.data),

  // Audit
  getAuditLog: (complaintId) => api.get(`/agents/complaints/${complaintId}/audit`).then((response) => response.data)
};

export default api; 
